import {
	cachedEndpointsService,
	UserService,
	ClientService,
	PaymentService,
	StripeService,
	PlaidService,
	CoordinatorService,
	UserFlowService,
	FirebaseService,
} from '@services';
import { thunks as thunksLocation } from '@redux/locations';
import { get, cloneDeep } from 'lodash';
import { isUserClient, updateProgressButton, apiErrorHandler } from '@config/utils';
import { appInfo } from '@config/envConfig';
import utils from '@utils';
import { push, replace } from 'react-router-redux';
import { STRIPE_KEY } from '@config';
import { ROUTES } from '@constants';
import Actions from '.';

/* ------------- Thunks actions ------------- */
const getUSStates = () => ((dispatch) => {
	dispatch(Actions.setLoading('USStates', true));

	cachedEndpointsService.getUSStates()
		.then(({ data }) => {
			const newData = utils.buildSelectArrayOptionsFromAPIResponse({
				data,
				labelProperty : 'Name',
				valueProperty : 'Id',
			});
			dispatch(Actions.updateStates(newData));
			dispatch(Actions.setLoading('USStates', false));
		})
		.catch(apiErrorHandler({
			dispatch,
			module       : 'app',
			defaultError : 'Unable to load states',
		}));
});

const getTimeZones = () => ((dispatch) => {
	cachedEndpointsService.getTimeZones()
		.then(({ data }) => {
			const newData = utils.buildSelectArrayOptionsFromAPIResponse({
				data,
				labelProperty : 'Description',
				valueProperty : 'Id',
			});
			dispatch(Actions.updateTimeZones(newData));
		})
		.catch(apiErrorHandler({
			dispatch,
			module       : 'app',
			defaultError : 'Unable to load states',
		}));
});

const getUser = (callback) => ((dispatch) => {
	UserService.getCurrent()
		.then(({ data : user }) => {
			dispatch(Actions.receiveUser(user));
			if (callback) callback();
		}).catch((error) => {
			if (callback) callback();
			apiErrorHandler({
				dispatch,
				module       : 'app',
				defaultError : 'Error fetching user data',
			})(error);
		});
});

export const initializeUserSession = (dispatch) => {
	if ((window.adb2cEnabled && localStorage.getItem('homeAccountId')) || localStorage.getItem('t')) {
		UserService.getCurrent()
			.then(({ data }) => {
			// Only allow login if the user is a Client
				if (isUserClient(data)) return Promise.resolve(data);
				return Promise.reject();
			})
			.then((user) => {
				/* TODO: Verify if this is needed; currently causing issues w/Impersonate
				// Logout the user if the version is changed
				if ( appInfo.version.toString() !== localStorage.getItem( 'appVersion' ) )
					dispatch( push( { pathname : '/logout' } ) );
				*/
				dispatch(Actions.receiveUser(user));
				dispatch(Actions.init());
				dispatch(Actions.login());
				dispatch(getUSStates());
				dispatch(getTimeZones());
				dispatch(thunksLocation.getMinimalLocationsData());
			})
			.catch(() => dispatch(Actions.init()));
	} else {
		dispatch(Actions.init());
	}
};

export const init = () => ((dispatch, getState, { msalInstance }) => {
	FirebaseService.getEnvironmentFeatureFlag('client_portal_azure_adb2c_enabled')
		.then((adb2cEnabledFlag) => {
			window.adb2cEnabled = adb2cEnabledFlag.asBoolean();
		})
		.catch(() => {
			window.adb2cEnabled = false;
		})
		.finally(() => initializeUserSession(dispatch, msalInstance));
});

export const login = ({
	username : email,
	password,
}, callback) => ((dispatch) => {
	updateProgressButton({
		dispatch,
		module   : 'app',
		property : 'loginData.progressButton',
		value    : 'loading',
	});

	UserService.login({
		email,
		password,
	})
		.then(({ data }) => {
		// Only allow login if the user is a Client
			if (isUserClient(data)) {
				localStorage.setItem('appVersion', appInfo.version);
				// Init Userflow service
				UserFlowService.initUserFlow(data);
				return Promise.resolve(data);
			}

			return Promise.reject(new Error({
				customNotification : {
					type    : 'error',
					message : 'This user is not allowed to use this portal',
					title   : 'Access Denied',
				},
			}));
		})
		.then((user) => {
			dispatch(Actions.login());
			dispatch(Actions.receiveUser(user));
			dispatch(thunksLocation.getMinimalLocationsData());
			dispatch(getUSStates());
			dispatch(getTimeZones());
			updateProgressButton({
				dispatch,
				module   : 'app',
				property : 'loginData.progressButton',
				value    : 'success',
			});
			if (callback) callback();
		})
		.catch((error) => {
			const message = error.body ? error.body.error_description : 'Login Failed';
			utils.notify({
				type  : 'error',
				title : 'Error',
				message,
			});
			updateProgressButton({
				dispatch,
				module   : 'app',
				property : 'loginData.progressButton',
				value    : 'error',
			});
		});
});

export const loginWithAD = () => ((dispatch) => {
	dispatch(Actions.setIsProcessingMsal(true));
	UserService.getCurrent()
		.then(({ data }) => {
		// Only allow login if the user is a Client
			if (isUserClient(data)) return Promise.resolve(data);
			return Promise.reject();
		})
		.then((user) => {
			/* TODO: Verify if this is needed; currently causing issues w/Impersonate
			// Logout the user if the version is changed
			if ( appInfo.version.toString() !== localStorage.getItem( 'appVersion' ) )
				dispatch( push( { pathname : '/logout' } ) );
			*/
			dispatch(Actions.receiveUser(user));
			dispatch(Actions.init());
			dispatch(Actions.login());
			dispatch(getUSStates());
			dispatch(getTimeZones());
			dispatch(thunksLocation.getMinimalLocationsData());
		})
		.catch(() => dispatch(Actions.init()))
		.finally(() => {
			dispatch(Actions.setIsProcessingMsal(false));
			dispatch(push({ pathname : ROUTES.root }));
		});
});

export const logout = () => ((dispatch, getState, { msalInstance }) => {
	const { adb2cEnabled } = window;
	UserService.logout()
		.then(() => {
			localStorage.removeItem('t');
		})
		.catch(apiErrorHandler({
			dispatch,
			module       : 'app',
			defaultError : 'Unable to logout, try again',
		}))
		.finally(() => {
			if (adb2cEnabled) {
				localStorage.removeItem('homeAccountId');
				msalInstance.logoutRedirect({ postLogoutRedirectUri : '/login' });
			} else {
				dispatch(Actions.logout());
				dispatch(replace({ pathname : '/' }));
			}
		});
});

export const unauthorizedEvent = () => ((dispatch, getState, { msalInstance }) => {
	if (window.adb2cEnabled) {
		msalInstance.browserStorage.clear().finally(() => {
			localStorage.removeItem('homeAccountId');
			dispatch(Actions.logout());
			dispatch(replace({ pathname : '/login' }));
		});
	}
});

const changePassword = () => ((dispatch, getState) => {
	const {
		changePassword : {
			password : oldPassword,
			newPassword,
		},
	} = getState().app;

	updateProgressButton({
		dispatch,
		module   : 'app',
		property : 'changePassword.buttonState',
		value    : 'loading',
	});

	UserService.updatePassword({
		oldPassword,
		newPassword,
	}).then(() => {
		updateProgressButton({
			dispatch,
			module   : 'app',
			property : 'changePassword.buttonState',
			value    : 'success',
		});
		dispatch(Actions.resetForm('changePassword'));
		utils.notify({
			type    : 'success',
			message : 'Password successfully updated',
		});
	}).catch((error) => {
		apiErrorHandler({
			dispatch,
			module       : 'app',
			defaultError : 'Error updating password',
		})(error);
		updateProgressButton({
			dispatch,
			module   : 'app',
			property : 'changePassword.buttonState',
			value    : 'error',
		});
	});
});

const updatePersonalSettings = (data) => ((dispatch) => {
	updateProgressButton({
		dispatch,
		module   : 'app',
		property : 'personal.buttonState',
		value    : 'loading',
	});

	UserService.updateProfile(data).then(() => {
		dispatch(getUser(() => {
			updateProgressButton({
				dispatch,
				module   : 'app',
				property : 'personal.buttonState',
				value    : 'success',
			});
			utils.notify({
				type    : 'success',
				message : 'Settings successfully updated',
			});
			setTimeout(() => dispatch(Actions.resetForm('personal')), 700);
		}));
	}).catch((error) => {
		apiErrorHandler({
			dispatch,
			module       : 'app',
			defaultError : 'Error updating personal settings',
		})(error);
		updateProgressButton({
			dispatch,
			module   : 'app',
			property : 'personal.buttonState',
			value    : 'error',
		});
	});
});

const updateClientDetails = ({ companyName, type = 'profile' }) => ((dispatch, getState) => {
	const {
		user : { Organization },
		bio  : { biography = undefined },
	} = getState().app;

	updateProgressButton({
		dispatch,
		module   : 'app',
		property : `${type}.buttonState`,
		value    : 'loading',
	});

	const data = {
		id                 : Organization.Id,
		name               : companyName || Organization.Name,
		primaryContactId   : Organization.PrimaryContactId,
		emergencyContactId : Organization.EmergencyContactId,
		status             : Organization.ClientStatus,
		organizationTypeId : Organization.OrganizationTypeId,
		organizationLogoId : Organization.OrganizationLogoId,
		biography          : type === 'profile' ? Organization.Biography : biography,
	};

	CoordinatorService.updateProfile(data).then((response) => {
		dispatch(getUser(() => {
			updateProgressButton({
				dispatch,
				module   : 'app',
				property : `${type}.buttonState`,
				value    : 'success',
			});
			utils.notify({
				type    : 'success',
				message : get(response, 'data.Message', `${type === 'profile' ? 'Profile' : 'Bio'} successfully updated`),
			});
			if (type === 'profile') setTimeout(() => dispatch(Actions.resetForm(type)), 700);
			else setTimeout(() => dispatch(Actions.setFormField(type, 'isEdit', false)), 700);
		}));
	}).catch((error) => {
		apiErrorHandler({
			dispatch,
			module       : 'app',
			defaultError : `Error updating ${type}`,
		})(error);
		updateProgressButton({
			dispatch,
			module   : 'app',
			property : `${type}.buttonState`,
			value    : 'error',
		});
	});
});

const getNotificationSettings = () => ((dispatch) => {
	dispatch(Actions.setFormField('notification', 'loading', true));
	UserService.getNotificationSettings()
		.then(({ data }) => {
			dispatch(Actions.updateNotificationSettings(data));
			dispatch(Actions.setFormField('notification', 'loading', false));
		}).catch(apiErrorHandler({
			dispatch,
			module       : 'app',
			property     : 'notification.loading',
			defaultError : 'Error fetching notification settings',
		}));
});

const updateNotifactionSettings = (notification, type) => ((dispatch, getState) => {
	const { notification : { settings } } = getState().app;
	const notificationSettings = cloneDeep(settings);
	const notificationMethodIndex = notificationSettings[notification].NotificationMethods.findIndex(({ MethodName }) => MethodName === type);
	const { Enabled } = notificationSettings[notification].NotificationMethods[notificationMethodIndex];

	// set toggle value
	notificationSettings[notification].NotificationMethods[notificationMethodIndex].Enabled = !Enabled;

	UserService.saveNotificationSettings(notificationSettings).then(({ data }) => {
		utils.notify({
			type    : 'success',
			message : get(data, 'Message', 'Notification settings updated successfully'),
		});

		dispatch(Actions.setFormField('notification', 'loading', false));
		dispatch(getNotificationSettings());
	}).catch(apiErrorHandler({
		dispatch,
		module       : 'app',
		property     : 'notification.loading',
		defaultError : 'Error updating notification settings',
	}));
});

const getPaymentMethods = () => ((dispatch) => {
	dispatch(Actions.setFormField('payment', 'loading', true));
	PaymentService.getData().then(({ data }) => {
		dispatch(Actions.setFormField('payment', 'data', data));
		dispatch(Actions.setFormField('payment', 'loading', false));
	}).catch(apiErrorHandler({
		dispatch,
		module       : 'app',
		property     : 'payment.loading',
		defaultError : 'Error fetching payment methods',
	}));
});

const setPreferredCard = (cardId) => ((dispatch) => {
	dispatch(Actions.setFormField('payment', 'loading', true));
	PaymentService.setPreferredCard(cardId).then(({ data }) => {
		utils.notify({
			type    : 'success',
			message : get(data, 'Message', 'Default card successfully updated'),
		});
		dispatch(getPaymentMethods());
	}).catch(apiErrorHandler({
		dispatch,
		module       : 'app',
		property     : 'payment.loading',
		defaultError : 'Error setting preferred card',
	}));
});

const verifyBankAccount = (data) => ((dispatch) => {
	dispatch(Actions.setFormField('payment', 'loading', true));
	PaymentService.verifyBankAccount(data).then(() => {
		utils.notify({
			type    : 'success',
			message : 'Bank account succesfully verified',
		});
		dispatch(getPaymentMethods());
	}).catch(apiErrorHandler({
		dispatch,
		module       : 'app',
		property     : 'payment.loading',
		defaultError : 'Error verifying bank account',
	}));
	setTimeout(() => dispatch(Actions.resetForm('bankAccountVerification')), 500);
});

const deletePaymentMethod = (id) => ((dispatch) => {
	dispatch(Actions.setFormField('payment', 'loading', true));
	PaymentService.deletePaymentMethod(id).then(() => {
		utils.notify({
			type    : 'success',
			message : 'Payment method successfully deleted',
		});
		dispatch(getPaymentMethods());
	}).catch(apiErrorHandler({
		dispatch,
		module       : 'app',
		property     : 'payment.loading',
		defaultError : 'Error deleting payment method',
	}));
});

const addPaymentMethodResponseHandler = (dispatch, type, token, IsRKPay, callback = undefined) => {
	if (token.error) {
		apiErrorHandler({
			dispatch,
			property       : 'loaders.addPaymentMethod',
			defaultMessage : 'Error getting token',
		})(token.error);
		return;
	}

	PaymentService.addPaymentMethod({ [type] : { TokenId : token.id, IsDefaultCard : true } })
		.then((response) => {
			utils.notify({
				type    : 'success',
				message : get(response, 'data.Message', 'Payment method added successfully'),
			});
			dispatch(Actions.setLoading('addPaymentMethod', false));
			dispatch(Actions.resetForm('addPayment'));

			if (callback) {
				callback();
			} else {
				dispatch(getPaymentMethods());
				if (!IsRKPay) {
					dispatch(getUser(() => {
						dispatch(push({ pathname : '/payments' }));
					}));
				}
			}
		}).catch(apiErrorHandler({
			dispatch,
			property       : 'loaders.addPaymentMethod',
			defaultMessage : 'Error adding payment method',
		}));
};

const addBankAccountWithPlaid = (PublicToken, AccountId, callback) => (async (dispatch, getState) => {
	const { app : { user : { IsRKPay } } } = getState();

	dispatch(Actions.setLoading('addPaymentMethod', true));

	try {
		const { data } = await PlaidService.exchangeToken({ PublicToken, AccountId });
		addPaymentMethodResponseHandler(dispatch, 'BankAccount', { id : data.BankAccountToken }, IsRKPay, callback);
	} catch (error) {
		apiErrorHandler({
			dispatch,
			property       : 'loaders.addPaymentMethod',
			defaultMessage : 'Error getting token',
		})(error);
	}
});

const addPaymentMethod = (formData, callback) => (async (dispatch, getState) => {
	const { app : { user : { IsRKPay } } } = getState();

	dispatch(Actions.setLoading('addPaymentMethod', true));

	if (formData.type === 'bankAccount') {
		const bankAccountToken = await StripeService.createBankAccountToken(STRIPE_KEY, formData);
		addPaymentMethodResponseHandler(dispatch, 'BankAccount', bankAccountToken, IsRKPay, callback);
	} else if (formData.type === 'creditCard') {
		StripeService.createCreditCardToken(STRIPE_KEY, formData, (creditCardToken) => {
			addPaymentMethodResponseHandler(dispatch, 'PaymentCard', creditCardToken, IsRKPay, callback);
		});
	}
});

const uploadCompanyImage = (file) => ((dispatch) => {
	dispatch(Actions.setFormField('profile', 'loading', true));
	ClientService.uploadImage({ file }).then(() => {
		dispatch(getUser(() => dispatch(Actions.setFormField('profile', 'loading', false))));
	}).catch(apiErrorHandler({
		dispatch,
		module       : 'app',
		property     : 'profile.loading',
		defaultError : 'Error uploading image',
	}));
});

const deleteCompanyLogo = () => ((dispatch) => {
	dispatch(Actions.setFormField('profile', 'loading', true));
	ClientService.deleteImage().then(() => dispatch(getUser(() => {
		dispatch(Actions.setFormField('profile', 'loading', false));
		utils.notify({
			type    : 'success',
			message : 'Company logo successfully removed',
		});
	}))).catch(apiErrorHandler({
		dispatch,
		module       : 'app',
		property     : 'profile.loading',
		defaultError : 'Error removing company logo',
	}));
});

const forgotPassword = () => ((dispatch, getState) => {
	const { forgotPassword : { email } } = getState().app;
	if (!email) {
		dispatch(Actions.changeForgotPasswordData({ validation : 'error' }));
		return;
	}
	dispatch(Actions.changeForgotPasswordData({ loading : true }));
	UserService.resetPassword(email).then(() => {
		dispatch(Actions.changeForgotPasswordData());
		dispatch(Actions.changeResetPasswordConfirmed(true));
	}).catch(apiErrorHandler({
		dispatch,
		module       : 'app',
		property     : 'forgotPassword.loading',
		defaultError : 'Error in sending reset password email',
	}));
});

export default {
	init,
	loginWithAD,
	login,
	logout,
	changePassword,
	updatePersonalSettings,
	updateClientDetails,
	getNotificationSettings,
	updateNotifactionSettings,
	getPaymentMethods,
	setPreferredCard,
	deletePaymentMethod,
	addBankAccountWithPlaid,
	addPaymentMethod,
	verifyBankAccount,
	uploadCompanyImage,
	deleteCompanyLogo,
	forgotPassword,
	getUser,
	getUSStates,
	getTimeZones,
	unauthorizedEvent,
};
